import { useEffect, useState } from 'react';
import { getAdmins, createAdmin, toggleAdmin } from '../../services/adminManagementService';
import { getErrorMessage } from '../../api/client';

const initialFormState = { name: '', email: '', password: '', role: 'admin' };

const AdminsTab = ({ admin }) => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const [formData, setFormData] = useState(initialFormState);
  const [isCreating, setIsCreating] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  const fetchAdmins = async () => {
    setIsLoading(true);
    setError('');
    setAccessDenied(false);
    try {
      const result = await getAdmins();
      setAdmins(result.data);
    } catch (fetchError) {
      if (fetchError.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError(getErrorMessage(fetchError, 'Unable to load admins.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setFormMessage('');
    setFormError('');
    try {
      const result = await createAdmin(formData);
      setFormMessage(`Admin created: ${result.data.email}`);
      setFormData(initialFormState);
      fetchAdmins();
    } catch (createError) {
      setFormError(getErrorMessage(createError, 'Unable to create admin.'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggle = async (id) => {
    setTogglingId(id);
    try {
      await toggleAdmin(id);
      fetchAdmins();
    } catch (toggleError) {
      setError(getErrorMessage(toggleError, 'Unable to update admin status.'));
    } finally {
      setTogglingId(null);
    }
  };

  if (accessDenied) {
    return (
      <div className="theme-state text-center p-3">
        Admin management is available to super_admin accounts only.
        {admin?.role ? ` You are signed in as "${admin.role}".` : ''}
      </div>
    );
  }

  return (
    <div>
      <div className="row g-4">
        <div className="col-lg-5">
          <div className="theme-mini-card p-4">
            <h5 className="text-white mb-3">Create New Admin</h5>
            <form onSubmit={handleCreate}>
              <div className="mb-3">
                <label className="form-label text-white fw-bold">Name</label>
                <input type="text" name="name" className="form-control theme-field px-3" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="mb-3">
                <label className="form-label text-white fw-bold">Email</label>
                <input type="email" name="email" className="form-control theme-field px-3" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="mb-3">
                <label className="form-label text-white fw-bold">Password</label>
                <input type="password" name="password" className="form-control theme-field px-3" value={formData.password} onChange={handleChange} required />
              </div>
              <div className="mb-3">
                <label className="form-label text-white fw-bold">Role</label>
                <select name="role" className="form-select theme-field px-3" value={formData.role} onChange={handleChange}>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              {formMessage && <div className="submission-message success">{formMessage}</div>}
              {formError && <div className="submission-message error">{formError}</div>}

              <button type="submit" className="btn theme-btn theme-btn-primary w-100" disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create Admin'}
              </button>
            </form>
          </div>
        </div>

        <div className="col-lg-7">
          {isLoading && <div className="theme-state text-center p-3">Loading admins...</div>}
          {!isLoading && error && <div className="theme-state theme-error text-center p-3">{error}</div>}

          {!isLoading && !error && (
            <div className="admin-panel-table-wrap">
              <table className="table table-hover mb-0 admin-panel-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>{a.email}</td>
                      <td>{a.role}</td>
                      <td>
                        <span className={`theme-status ${a.is_active ? 'paid' : 'unknown'}`}>
                          {a.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-action-btn"
                          disabled={togglingId === a.id}
                          onClick={() => handleToggle(a.id)}
                        >
                          {togglingId === a.id ? 'Updating...' : a.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminsTab;
